import { useContext, useState } from "react";
import useAPIRequest from "../../../custom_hooks/simple/useAPIRequest";
import UiContext from "../../../store/ui-context";
import { useSelector } from "react-redux";
import { format } from "date-fns";

const defaultTake = 10;

export const useAPIGetStockIn = () => {
  const uiContext = useContext(UiContext);
  const { requestGet, loading } = useAPIRequest();
  const employee = useSelector((state) => state.auth.employee);

  const [loadingPending, setLoadingPending] = useState(false);

  const [filterValue, setFilterValue] = useState({
    number: null,
    warehouse_id: null,
    data_warehouse: null,
    tanggal_from: new Date(),
    tanggal_to: new Date(),
  });

  const [filterPendingValue, setFilterPendingValue] = useState({
    number: null,
    warehouse_id: null,
    data_warehouse: null,
    verification_type: null,
  });

  const [paging, setPaging] = useState({
    page: 1,
    take: defaultTake,
    total: 0,
    filter: "",
  });
  const [listData, setListData] = useState([]);
  const [listDataPending, setListDataPending] = useState([]);

  //  API call section

  const fetchListData = async ({ page, take, filter }) => {
    let newFilter = filter ?? getFilter(filterValue);

    let newPendingFilter = getPendingFilter(filterValue);

    // if (employee?.user_type == 3) {
    //   newFilter = `data_customer.bc_id:=:${employee?.id};${newFilter}`;
    // }

    requestGet({
      fullUrl: `/api/stock/stockin/data`,
      params: {
        page: page,
        take: take,
        filter: `${newFilter};verif_owner_id:!=:Null;verif_payment_by_id:!=:Null;verif_shipment_by_id:!=:Null;verif_done_by_id:!=:Null`,
        order: "id",
        order_method: "ASC",
        // PART_WITH_FETCH
        with_branch: 1,
        with_warehouse: 1,
        "with_stock_in_details,data_product": 1,
        with_verif_owner: 1,
        with_verif_payment_by: 1,
        with_verif_shipment_by: 1,
        with_verif_done_by: 1,
      },
      onSuccess: ({ pagination, message, data }) => {
        setPaging({
          ...paging,
          total: pagination["total"],
          page: pagination["page"],
          take: pagination["take"],
          filter: newFilter,
        });
        const newData = data.map((el, index) => {
          return {
            ...el,
            no: (page - 1) * take + index + 1,
          };
        });
        setListData(newData);
      },
      onError: ({ message, data }) => uiContext.showErrorMsg("Error", message),
    });

    setLoadingPending(true);
    requestGet({
      fullUrl: `/api/stock/stockin/data`,
      params: {
        page: 1,
        take: 10000,
        filter: newPendingFilter,
        order: "id",
        order_method: "ASC",
        with_branch: 1,
        with_warehouse: 1,
        "with_stock_in_details,data_product": 1,
        with_verif_owner: 1,
        with_verif_payment_by: 1,
        with_verif_shipment_by: 1,
        with_verif_done_by: 1,
      },
      onSuccess: ({ data }) => {
        const newData = data.map((el, index) => ({
          ...el,
          no: (paging.page - 1) * paging.take + index + 1,
        }));
        setListDataPending(newData);
        setLoadingPending(false);
      },
      onError: ({ message }) => {
        uiContext.showErrorMsg("Error", message);
        setLoadingPending(false);
      },
    });

  };

  // other function section

  const getFilter = (_filterValue) => {
    const filterArr = [];

    const newFilterValue = _filterValue ?? filterValue;

    if (newFilterValue.number) {
      filterArr.push(`number:contains:${newFilterValue.number}`);
    }

    if (newFilterValue.warehouse_id) {
      filterArr.push(`warehouse_id:=:${newFilterValue.warehouse_id}`);
    }

    if (newFilterValue.tanggal_from) {
      const formattedDate = format(newFilterValue.tanggal_from, "yyyy-MM-dd");
      filterArr.push(`transaction_date:>=:${formattedDate}`);
    }

    if (newFilterValue.tanggal_to) {
      const formattedDate = format(newFilterValue.tanggal_to, "yyyy-MM-dd");
      filterArr.push(`transaction_date:<=:${formattedDate}`);
    }

    return filterArr.join(";");
  };

  const getPendingFilter = (_filterValue) => {
    const filterArr = [];
    const f = filterPendingValue;

    if (f.number) {
      filterArr.push(`number:contains:${f.number}`);
    }

    if (f.warehouse_id) {
      filterArr.push(`warehouse_id:=:${f.warehouse_id}`);
    }

    // 🔥 If typed filter exists → use it
    if (f.verification_type && f.verification_type !== "all") {
      const chain = {
        owner: [
          "verif_owner_id:=:Null",
        ],
        payment: [
          "verif_owner_id:!=:Null",
          "verif_payment_by_id:=:Null",
        ],
        shipment: [
          "verif_owner_id:!=:Null",
          "verif_payment_by_id:!=:Null",
          "verif_shipment_by_id:=:Null",
        ],
        done: [
          "verif_owner_id:!=:Null",
          "verif_payment_by_id:!=:Null",
          "verif_shipment_by_id:!=:Null",
          "verif_done_by_id:=:Null",
        ],
      };

      filterArr.push(chain[f.verification_type].join(";"));
    } else {
      // No verification type: show ANY PENDING on ANY step
      filterArr.push(
        [
          "verif_owner_id:=:Null",
          "verif_payment_by_id:=:Null",
          "verif_shipment_by_id:=:Null",
          "verif_done_by_id:=:Null",
        ].join("||")
      );
    }

    return filterArr.join(";");
  };

  // handle ui event section

  const handleOnClickRefresh = async () => {
    fetchListData({ page: 1, take: defaultTake });
  };

  const handleOnPage = (e) => {
    const newPage = e.first / paging.take + 1;
    const newTake = e.rows;

    setPaging({ ...paging, take: newTake, page: newPage });
    fetchListData({ page: newPage, take: newTake, filter: paging.filter });
  };

  const handleOnUpdateViewRow = (row) => {
    fetchListData({ page: paging.page, take: paging.take, filter: paging.filter });
    // let foundInList = false;
    // let newListData = listData.map((el) => {
    //   if (el.id === row.id) {
    //     foundInList = true;
    //     return row;
    //   }
    //   return el;
    // });

    // if (!foundInList) {
    //   if (newListData.length > 0) {
    //     newListData.unshift(row);
    //   } else {
    //     newListData.push(row);
    //   }
    // }

    // // reorder no
    // newListData = newListData.map((el, index) => {
    //   return {
    //     ...el,
    //     no: (paging.page - 1) * paging.take + index + 1,
    //   };
    // });

    // setListDataPending(newListData);
  };

  const handleOnDeleteViewRow = (row) => {
    fetchListData({ page: paging.page, take: paging.take, filter: paging.filter });
    // let newListData = [...listData];

    // const index = newListData.findIndex((el) => el.id === row.id);
    // if (index > -1) {
    //   newListData.splice(index, 1);
    // }

    // // reorder no
    // newListData = newListData.map((el, index) => {
    //   return {
    //     ...el,
    //     no: (paging.page - 1) * paging.take + index + 1,
    //   };
    // });

    // setListDataPending(newListData);
  };

  const handleVerifOwner = async (id, onSuccess) => {
    requestGet({
      fullUrl: `/api/stock/stockin/verify/${id}/owner`,
      onSuccess: ({ message, data }) => {
        uiContext.showSuccessMsg("Success", message);
        if (onSuccess) onSuccess(data);
      },
      onError: ({ message, data }) => uiContext.showErrorMsg("Error", message),
    });
  };

  const handleVerifPayment = async (id, onSuccess) => {
    requestGet({
      fullUrl: `/api/stock/stockin/verify/${id}/payment`,
      onSuccess: ({ message, data }) => {
        uiContext.showSuccessMsg("Success", message);
        if (onSuccess) onSuccess(data);
      },
      onError: ({ message, data }) => uiContext.showErrorMsg("Error", message),
    });
  };

  const handleVerifShipment = async (id, onSuccess) => {
    requestGet({
      fullUrl: `/api/stock/stockin/verify/${id}/shipment`,
      onSuccess: ({ message, data }) => {
        uiContext.showSuccessMsg("Success", message);
        if (onSuccess) onSuccess(data);
      },
      onError: ({ message, data }) => uiContext.showErrorMsg("Error", message),
    });
  };

  const handleVerifDone = async (id, onSuccess) => {
    requestGet({
      fullUrl: `/api/stock/stockin/verify/${id}/done`,
      onSuccess: ({ message, data }) => {
        uiContext.showSuccessMsg("Success", message);
        if (onSuccess) onSuccess(data);
      },
      onError: ({ message, data }) => uiContext.showErrorMsg("Error", message),
    });
  };

  return {
    loading,
    loadingPending,
    listData,
    listDataPending,
    fetchListData,
    filterValue,
    setFilterValue,
    filterPendingValue,
    setFilterPendingValue,
    handleOnClickRefresh,
    handleOnPage,
    paging,
    handleOnUpdateViewRow,
    handleOnDeleteViewRow,
    handleVerifOwner,
    handleVerifPayment,
    handleVerifShipment,
    handleVerifDone,
  };
};
